import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from './baseQuery';
import { apiConstants, post, postMultipart, patch } from '../common/constants';
import { foldIndependentRvpIntoRows } from '../pages/merchants/pricing-wizard/helpers/rateEngine';
import {
  setMerchantId,
  setMerchant,
  submitKyc as submitKycReducer,
  setCommit,
  recordConfirmation,
  setLastSaved,
  setPickupRegistered,
  setAgreementS3Key,
  setAgreementJobId,
  setExtractionStatus,
  setAgreement,
  setRvpEnabled,
  setRvpMode,
  updateRvpFormula,
  updateBlendedTc,
  updatePerCourierTc,
  setRateCardRows,
  setEffectiveMode,
  setEffectiveDate,
  updateRto,
  updateCredit,
  setCodSplit,
  setCreditApprovalS3Key,
  setInvoiceCeiling,
} from './pricingWizardSlice';

// Strips null / undefined / empty-string / zero / empty-array values before sending.
const cleanPayload = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => {
    if (v === null || v === undefined || v === '' || v === 0) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  })
);

// Backend MerchantCategoryEnum accepts only ENTERPRISE | SME. Map the UI's
// enterprise-size tiers (MICRO/SMALL/MEDIUM/LARGE) to a valid enum so saving
// master-info doesn't fail. Legacy ENTERPRISE/SME pass through unchanged.

// Set REACT_APP_WIZARD_API_MODE=mock in .env to use demo/mock responses.
// Default is live — real endpoints are called unless mock mode is explicitly enabled.
const MOCK = process.env.REACT_APP_WIZARD_API_MODE === 'mock';
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

export const wizardApiSlice = createApi({
  reducerPath: 'wizardApi',
  baseQuery,
  tagTypes: ['MerchantPricing'],
  endpoints: (builder) => ({

    // ── Stage 0 — Create merchant ─────────────────────────────────────────────
    createMerchant: builder.mutation({
      queryFn: async (signup, api, _extra, bq) => {
        if (MOCK) {
          await delay(900);
          return {
            data: {
              _id: 'mock_' + Date.now(),
              merchantId: 'MERCH-' + Date.now().toString().slice(-6),
              name: signup.business_name,
              brand: signup.brand || signup.business_name,
              status: 'CREATED',
            },
          };
        }
        // Live: POST /api/merchant/create
        try {
          const { masterInfo } = api.getState().pricingWizard;
          const res = await bq({
            url: apiConstants.MERCHANT_CREATE,
            data: cleanPayload({
              name: signup.brand || signup.business_name,
              email: signup.email,
              contact_no: signup.contact,
              address: signup.address,
              pincode: Number(signup.pincode) || null,
              city: signup.city,
              state: signup.state,
              companyName: signup.business_name,
              country: 'India',
              orderSource: masterInfo.order_sources?.length ? masterInfo.order_sources : undefined,
              merchantCategory: masterInfo.category || undefined,
              aov: masterInfo.aov || undefined,
              unitsPerOrder: masterInfo.units_per_order || undefined,
              monthlyShipmentVolume: masterInfo.monthly_volume || undefined,
              integrationType: masterInfo.integration_type || undefined,
              storefrontPlatform: masterInfo.storefront_platform || undefined,
              omsPlatforms: masterInfo.oms_platforms?.length ? masterInfo.oms_platforms : undefined,
              customOms: masterInfo.custom_oms?.length ? masterInfo.custom_oms : undefined,
              proZoWalletSharePct: masterInfo.prozo_wallet_share_pct ?? undefined,
            }),
            type: 'POST',
          });
          return { data: res.data ?? res };
        } catch (err) {
          // Return the full response body so err.data.message is available in .unwrap() catch
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_signup, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const merchantId = data.id || data._id;

          dispatch(setMerchantId(merchantId));
          dispatch(setMerchant({
            id: merchantId,
            code: String(data.merchantId || ''),
            brand: data.name,
            legal: data.companyName || data.name,
            email: data.email,
            contact_no: data.contact_no,
            status: data.status,
          }));

          // Bootstrap the onboarding session immediately after merchant creation.
          // This is a protected call (admin JWT required) split from the public
          // /merchant/create endpoint so the session record is always initialised.
          try {
            await post(`${apiConstants.MERCHANT}/${merchantId}/onboarding/init`);
          } catch { /* non-blocking — session init failure does not block the wizard */ }
        } catch { /* error is surfaced via isError on the mutation hook */ }
      },
    }),

    // ── Stage 0 — Update master info (order sources, category, AOV, volume) ────
    // Called on Stage 0 Next when merchant already exists.
    // Only sends masterInfo fields — does NOT touch profile/warehouse/service fields
    // which are not yet filled at this stage.
    updateMasterInfoOnly: builder.mutation({
      queryFn: async (_arg, api, _extra, _bq) => {
        if (MOCK) { await delay(300); return { data: { updated: true } }; }
        const { pricingWizard: { merchantId, masterInfo } } = api.getState();
        try {
          const res = await patch(apiConstants.MERCHANT_UPDATE, cleanPayload({
            id: merchantId,
            orderSource: masterInfo.order_sources?.length ? masterInfo.order_sources : undefined,
            merchantCategory: masterInfo.category || undefined,
            aov: masterInfo.aov || undefined,
            monthlyShipmentVolume: masterInfo.monthly_volume || undefined,
            unitsPerOrder: masterInfo.units_per_order || undefined,
            integrationType: masterInfo.integration_type || undefined,
            storefrontPlatform: masterInfo.storefront_platform || undefined,
            omsPlatforms: masterInfo.oms_platforms?.length ? masterInfo.oms_platforms : undefined,
            customOms: masterInfo.custom_oms?.length ? masterInfo.custom_oms : undefined,
            proZoWalletSharePct: masterInfo.prozo_wallet_share_pct ?? undefined,
          }));
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 1 — Submit KYC ─────────────────────────────────────────────────
    // Two sequential API calls:
    //   1. POST merchant/submit_kyc_details/{merchantId}  — multipart file upload
    //   2. POST merchant/addMandatoryFields               — JSON text fields
    // `files` arg: { pan, gstin, bank, signatory, msme } — actual File objects from component state
    submitKyc: builder.mutation({
      queryFn: async ({ files = {}, companyType = '' } = {}, api, _extra, _bq) => {
        if (MOCK) {
          await delay(600);
          return { data: { submitted: true, submittedAt: new Date().toISOString() } };
        }
        const { pricingWizard: { merchantId, kyc } } = api.getState();
        try {
          // ── API 1: file upload ──────────────────────────────────────────────
          const formData = new FormData();
          if (files.pan)       formData.append('PAN',              files.pan);
          if (files.gstin)     formData.append('GST',              files.gstin);
          if (files.bank)      formData.append('Bank Details',     files.bank);
          if (files.signatory) formData.append('Aadhar Card',      files.signatory);
          if (files.msme && !kyc.docs.msme.not_registered) {
            formData.append('MSME Certificate', files.msme);
          }
          // Additional docs — keyed as `additional:${id}` in kycFileMap; metadata from kyc.additionalDocs
          const additionalDocs = kyc.additionalDocs || [];
          additionalDocs.forEach((doc, idx) => {
            const file = files[`additional:${doc.id}`];
            if (!file) return;
            formData.append(`additionalDoc_${idx}_file`, file);
            formData.append(`additionalDoc_${idx}_type`, doc.type || '');
            formData.append(`additionalDoc_${idx}_remark`, doc.remark || '');
          });

          console.log('[KYC API 1] POST merchant/submit_kyc_details/' + merchantId, {
            merchantId,
            filesBeingSent: Object.fromEntries(
              Object.entries(files).filter(([, v]) => v).map(([k, v]) => [k, v?.name])
            ),
          });

          const uploadRes = await postMultipart(`${apiConstants.KYC_UPLOAD}/${merchantId}`, formData);
          console.log('[KYC API 1] Response:', uploadRes?.data);

          // ── API 2: text fields — fires only after API 1 succeeds ─────────────
          const textPayload = {
            id: merchantId,
            companyType,
            pan:    kyc.docs.pan.number    || '',
            gst:    kyc.docs.gstin.number  || '',
            aadhar: kyc.docs.signatory.aadhaar_no || kyc.docs.signatory.aadhaar_last4 || '',
            msmeNumber: kyc.docs.msme.not_registered ? undefined : (kyc.docs.msme.number || undefined),
            msmeNotRegistered: !!kyc.docs.msme.not_registered,
          };
          console.log('[KYC API 2] POST merchant/addMandatoryFields', textPayload);

          const textRes = await post(apiConstants.MERCHANT_ADD_MANDATORY_FIELDS, textPayload);
          console.log('[KYC API 2] Response:', textRes?.data);

          return { data: { submitted: true, submittedAt: new Date().toISOString() } };
        } catch (err) {
          console.error('[KYC Submit Error]', err?.response?.data || err?.message);
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(submitKycReducer());
        } catch { /* error surfaced via isError on the mutation hook */ }
      },
    }),

    // ── Stage 7 — Commit rate card ───────────────────────────────────────────
    // Writes rate_card_version + rate_card_row + terms_and_conditions in one call.
    commitRateCard: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        const { pricingWizard: w } = api.getState();
        if (MOCK) {
          await delay(1400);
          return {
            data: {
              version: 'v' + Date.now().toString().slice(-6),
              written: w.rateCard.rows.length,
              failures: 0,
              ts: new Date().toISOString(),
            },
          };
        }
        // Live: POST /api/merchant/{id}/rate-card/commit
        // Adjust endpoint path once backend team confirms the route.
        return bq({
          url: `${apiConstants.MERCHANT}/${w.merchantId}/rate-card/commit`,
          data: {
            rows: w.rateCard.rows,
            tcs: w.tc,
            rto: w.rto,
            rvp: w.rvp,
            arrangement: w.arrangement,
            resolvedServices: w.resolvedServices,
            credit: w.credit,
            agreement: {
              gst_stance: w.agreement.gst_stance,
              weight_ceiling: w.agreement.weight_ceiling,
              vc: w.agreement.vc,
            },
            effective_date: w.effective_date,
            effective_mode: w.effective_mode,
            invoice_ceiling: w.invoice_ceiling,
          },
          type: 'POST',
        });
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;
          const { rateCard, merchant } = getState().pricingWizard;
          dispatch(setCommit({
            version: data.version,
            written: data.written,
            rows: rateCard.rows.length,
            ts: data.ts,
            failures: data.failures,
          }));
          dispatch(setMerchant({ ...(merchant || {}), committed: true, status: 'Active' }));
          dispatch(recordConfirmation('commit_to_proship'));
        } catch { /* error is surfaced via isError on the mutation hook */ }
      },
    }),

    // ── Stage 0 — Register pickup address ────────────────────────────────────
    // Called right after createMerchant succeeds. Non-blocking — failure does not
    // prevent the wizard from advancing.
    registerPickup: builder.mutation({
      queryFn: async ({ merchantId, ...payload }, _api, _extra, bq) => {
        if (MOCK) {
          await delay(400);
          return { data: { registered: true } };
        }
        try {
          const res = await bq({
            url: `${apiConstants.PICKUP_LOCATION}/${merchantId}`,
            data: payload,
            type: 'POST',
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(setPickupRegistered(true));
        } catch { /* silent — pickup failure doesn't block the wizard */ }
      },
    }),

    // ── Incremental draft save — fires on every Next click (fire-and-forget) ──
    // Draft endpoint not yet available on backend — persisted to localStorage only.
    // When the backend endpoint is ready, replace the queryFn body with a real bq call.
    saveDraft: builder.mutation({
      queryFn: async () => {
        await delay(100);
        return { data: { saved: true, ts: new Date().toISOString() } };
      },
      onQueryStarted: async (_payload, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(setLastSaved());
        } catch {
          // Draft save failures are silent — never block the user
        }
      },
    }),

    // ── Update mode — load existing merchant pricing ─────────────────────────
    loadMerchantPricing: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        if (MOCK) {
          await delay(400);
          return { data: null }; // null = use wizard defaults in demo
        }
        // Live: GET /api/merchant/{id}/pricing
        return bq({ url: `${apiConstants.MERCHANT}/${merchantId}/pricing`, type: 'GET' });
      },
      onQueryStarted: async (_merchantId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (!data) return;
          // Mark reloaded rows as a real saved source (not engine-built) so Stage 4 shows
          // them verbatim and does NOT re-derive from agreement.extracted (which diverges
          // — lane collapse, slab fallbacks). The preview must equal what was committed.
          if (data.rateCard?.rows?.length) {
            // RVP field names are asymmetric: save sends rvp_qc/rvp_noqc, but
            // GET /pricing returns them as with_qc/without_qc. Remap on reload so
            // the rate-card grid + review repopulate the RVP columns correctly.
            const rows = data.rateCard.rows.map((r) => {
              const { with_qc, without_qc, ...rest } = r;
              return {
                ...rest,
                rvp_qc: with_qc != null ? with_qc : r.rvp_qc,
                rvp_noqc: without_qc != null ? without_qc : r.rvp_noqc,
              };
            });
            dispatch(setRateCardRows({ rows, source: 'persisted' }));
          }
          if (data.pricingEffectiveMode) {
            dispatch(setEffectiveMode(data.pricingEffectiveMode.toUpperCase() === 'IMMEDIATE' ? 'immediate' : 'from_date'));
          }
          if (data.pricingGoLiveDate) dispatch(setEffectiveDate(data.pricingGoLiveDate));
        } catch { /* error is surfaced via isError on the query hook */ }
      },
      providesTags: ['MerchantPricing'],
    }),

    // ── Stage 1c — Update bank details ────────────────────────────────────────
    updateBankDetails: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        if (MOCK) { await delay(400); return { data: { updated: true } }; }
        const { pricingWizard: { merchantId, kyc } } = api.getState();
        try {
          const res = await patch(apiConstants.MERCHANT_UPDATE_BANK, {
            merchantId,
            bankName: kyc.docs.bank.bank_name || '',
            beneficiaryName: kyc.docs.bank.beneficiary_name || '',
            accountNumber: kyc.docs.bank.account_no || '',
            ifscCode: kyc.docs.bank.ifsc || '',
            accountType: kyc.docs.bank.account_type || 'CURRENT',
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 2a — Update merchant profile + service + warehouse ──────────────
    updateMerchantProfile: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        if (MOCK) { await delay(500); return { data: { updated: true } }; }
        const { pricingWizard: { merchantId, masterInfo, profile, resolvedServices, warehouse } } = api.getState();
        const freightDirections = [
          ...(profile.directions.forward      ? ['FORWARD'] : []),
          ...(profile.directions.reverse_noqc ? ['REVERSE'] : []),
          ...(profile.directions.reverse_qc   ? ['RVP']     : []),
        ];
        try {
          const res = await patch(apiConstants.MERCHANT_UPDATE, cleanPayload({
            id: merchantId,
            orderSource: masterInfo.order_sources?.length ? masterInfo.order_sources : undefined,
            merchantCategory: masterInfo.category || undefined,
            aov: masterInfo.aov || undefined,
            monthlyShipmentVolume: masterInfo.monthly_volume || undefined,
            unitsPerOrder: masterInfo.units_per_order || undefined,
            freightDirections: freightDirections.length ? freightDirections : undefined,
            paymentMode: profile.payment || undefined,
            resolvedServiceTypes: resolvedServices.length ? resolvedServices.map((s) => s.id) : undefined,
            warehouseMode: warehouse.is_prozo === 'yes' ? 'PROZO' : warehouse.is_prozo === 'no' ? 'NON_PROZO' : 'MIXED',
            selectedWarehouseCodes: warehouse.prozo_warehouses?.length ? warehouse.prozo_warehouses : undefined,
            integrationType: masterInfo.integration_type || undefined,
            storefrontPlatform: masterInfo.storefront_platform || undefined,
            omsPlatforms: masterInfo.oms_platforms?.length ? masterInfo.oms_platforms : undefined,
            customOms: masterInfo.custom_oms?.length ? masterInfo.custom_oms : undefined,
            proZoWalletSharePct: masterInfo.prozo_wallet_share_pct ?? undefined,
          }));
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 3a — Upload agreement PDF ──────────────────────────────────────
    uploadAgreement: builder.mutation({
      queryFn: async ({ merchantId, file }, _api, _extra, bq) => {
        if (MOCK) {
          await delay(1200);
          return { data: { s3Key: `${merchantId}/agreement/mock_${Date.now()}.pdf`, uploadedAt: new Date().toISOString() } };
        }
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await postMultipart(`${apiConstants.MERCHANT}/${merchantId}/upload-agreement`, formData);
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.s3Key) dispatch(setAgreementS3Key(data.s3Key));
        } catch { /* surfaced via isError */ }
      },
    }),

    // ── Stage 3a-2 — Upload credit approval email (png / pdf) ────────────────
    uploadCreditApproval: builder.mutation({
      queryFn: async ({ merchantId, file }, _api, _extra, bq) => {
        if (MOCK) {
          await delay(800);
          return { data: { s3Key: `${merchantId}/credit-approval/mock_${Date.now()}`, uploadedAt: new Date().toISOString() } };
        }
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await postMultipart(`${apiConstants.MERCHANT}/${merchantId}/upload-credit-approval`, formData);
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.s3Key) dispatch(setCreditApprovalS3Key(data.s3Key));
        } catch { /* surfaced via isError */ }
      },
    }),

    // ── Stage 3b — Trigger AI extraction ─────────────────────────────────────
    analyzeAgreement: builder.mutation({
      queryFn: async ({ merchantId, s3Key }, _api, _extra, bq) => {
        if (MOCK) {
          await delay(600);
          return { data: { jobId: `mock_job_${Date.now()}`, status: 'IN_PROGRESS', merchantId } };
        }
        try {
          const res = await post(`${apiConstants.MERCHANT}/${merchantId}/analyze-agreement`, { s3Key });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.jobId) dispatch(setAgreementJobId(data.jobId));
          // Mark IN_PROGRESS immediately so the loader shows without waiting for the first poll
          dispatch(setExtractionStatus('IN_PROGRESS'));
        } catch { /* surfaced via isError */ }
      },
    }),

    // ── Stage 3c — Poll extraction status ────────────────────────────────────
    // jobId is optional — omit to resume the most recent job for this merchant.
    // Status values: IN_PROGRESS → SUCCEEDED | FAILED
    getAgreementAnalysis: builder.query({
      queryFn: async ({ merchantId, jobId }, _api, _extra, bq) => {
        if (MOCK) {
          await delay(800);
          return {
            data: {
              id: jobId || `mock_job_${Date.now()}`,
              merchantOid: merchantId,
              status: 'SUCCEEDED',
              result: {
                brandName: 'Mock Brand',
                confidence: 0.91,
                detectedServices: ['FWD_SURFACE_REG', 'FWD_AIR_REG'],
                mainPageFields: {
                  gstStance: 'EXCLUSIVE',
                  weightCeiling: 20000,
                  volumetricCoefficient: 5000,
                  rvpEnabled: false,
                  rvpMode: 'FORMULA',
                  codRemittanceDefaultDays: 7,
                  codRemittanceFreqPerMonth: 4,
                },
                globalTAndC: {},
                courierTabs: [],
              },
            },
          };
        }
        try {
          const url = jobId
            ? `${apiConstants.MERCHANT}/${merchantId}/agreement-analysis?jobId=${jobId}`
            : `${apiConstants.MERCHANT}/${merchantId}/agreement-analysis`;
          const res = await bq({ url, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.status) dispatch(setExtractionStatus(data.status));
          if (data.id) dispatch(setAgreementJobId(data.id));

          // Map SUCCEEDED result to wizard Redux state
          if (data.status === 'SUCCEEDED' && data.result) {
            const { mainPageFields: mf = {}, confidence, detectedServices = [], courierTabs = [], globalTAndC } = data.result;

            // Transform courierTabs array → nested { parent: { zoneId: { slabKey: price } } }
            // that buildRateCardRows() expects.
            const rates = {};
            const slabKeySet = new Set();
            courierTabs.forEach((tab) => {
              const parent = tab.courierName;
              if (!parent) return;
              rates[parent] = {};
              (tab.rateTable || []).forEach((row) => {
                const slabKey = `${row.slabStartGms}-${row.slabEndGms}`;
                slabKeySet.add(slabKey);
                for (let z = 1; z <= 5; z++) {
                  if (!rates[parent][z]) rates[parent][z] = {};
                  if (row[`z${z}`] != null) rates[parent][z][slabKey] = row[`z${z}`];
                }
              });
            });
            const slabKeys = [...slabKeySet].sort((a, b) => Number(a.split('-')[0]) - Number(b.split('-')[0]));

            dispatch(setAgreement({
              confidence: confidence ?? 0,
              detected_services: detectedServices,
              gst_stance: (mf.gstStance || 'EXCLUSIVE').toLowerCase(),
              vc: mf.volumetricCoefficient || 5000,
              weight_ceiling: mf.weightCeiling || 20000,
              extracted: {
                rates: Object.keys(rates).length ? rates : null,
                slabKeys: slabKeys.length ? slabKeys : null,
                tcs: globalTAndC || null,
                courierTabs: courierTabs.length ? courierTabs : null,
              },
            }));

            if (mf.rvpEnabled != null) dispatch(setRvpEnabled(!!mf.rvpEnabled));
            if (mf.rvpMode) dispatch(setRvpMode(mf.rvpMode === 'FORMULA' ? 'forward_linked' : 'independent'));
            if (mf.rvpMode === 'FORMULA' && (mf.rvpFlatCharge != null || mf.rvpMultiplier != null)) {
              const flat = mf.rvpFlatCharge ?? 0;
              const mult = mf.rvpMultiplier ?? 1;
              const type = flat > 0 && mult > 1 ? 'higher' : mult > 1 ? 'mult' : 'add';
              dispatch(updateRvpFormula({ parent: '__blended__', patch: { type, flat, mult } }));
            }

            // Pre-fill tc.blended from globalTAndC
            if (globalTAndC) {
              const gtc = globalTAndC;
              dispatch(updateBlendedTc({
                ...(gtc.codBaseCharge  != null && { cod_fixed:   gtc.codBaseCharge }),
                ...(gtc.codPct         != null && { cod_pct:     gtc.codPct }),
                ...(gtc.codOperator         && { cod_op:      gtc.codOperator }),
                ...(gtc.rtoChargeOfFwdPct != null && { rto_pct: gtc.rtoChargeOfFwdPct }),
                ...(gtc.rvpFlat        != null && { rvp_flat:   gtc.rvpFlat }),
                ...(gtc.rvpPctOfFwd    != null && { rvp_pct:    gtc.rvpPctOfFwd }),
                ...(gtc.qcCharge       != null && { qc:         gtc.qcCharge }),
                ...(gtc.fuelSurchargePct != null && { fuel_pct: gtc.fuelSurchargePct }),
                ...(gtc.docketCharge   != null && { docket:     gtc.docketCharge }),
              }));
              if (gtc.rtoChargeOfFwdPct != null) dispatch(updateRto({ mode: 'pct_fwd', pct_fwd: gtc.rtoChargeOfFwdPct }));
            }

            // Pre-fill tc.perCourier from each courier tab's tandC
            courierTabs.forEach((tab) => {
              if (!tab.tandC || !tab.courierName) return;
              const tc = tab.tandC;
              dispatch(updatePerCourierTc({
                parent: tab.courierName,
                patch: {
                  ...(tc.codBaseCharge  != null && { cod_fixed:   tc.codBaseCharge }),
                  ...(tc.codPct         != null && { cod_pct:     tc.codPct }),
                  ...(tc.codOperator         && { cod_op:      tc.codOperator }),
                  ...(tc.rtoChargeOfFwdPct != null && { rto_pct: tc.rtoChargeOfFwdPct }),
                  ...(tc.rvpFlat        != null && { rvp_flat:   tc.rvpFlat }),
                  ...(tc.rvpPctOfFwd    != null && { rvp_pct:    tc.rvpPctOfFwd }),
                  ...(tc.qcCharge       != null && { qc:         tc.qcCharge }),
                  ...(tc.fuelSurchargePct != null && { fuel_pct: tc.fuelSurchargePct }),
                  ...(tc.docketCharge   != null && { docket:     tc.docketCharge }),
                },
              }));
            });
          }
        } catch { /* surfaced via isError */ }
      },
    }),

    // ── Stage 3d — Confirm pricing commercials ───────────────────────────────
    confirmPricingCommercials: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        if (MOCK) { await delay(600); return { data: { success: true } }; }
        const { pricingWizard: { merchantId, agreement, credit, arrangement, rvp, resolvedServices, rateCardMode } } = api.getState();  // eslint-disable-line no-unused-vars
        // Apply default arrangement for any service the user left untouched. The mode
        // must follow the Blended/Courier-wise radio — the backend derives the merchant's
        // rateCardMode from it (previously hardcoded 'cp', which mislabelled blended merchants).
        const defaultMode = rateCardMode === 'blended' ? 'blended' : 'cp';
        const effectiveArrangement = (resolvedServices || []).reduce((acc, s) => {
          acc[s.id] = arrangement[s.id] || { type: 'ZWZ', mode: defaultMode, ceiling: null };
          return acc;
        }, {});
        try {
          const res = await post(`${apiConstants.MERCHANT}/${merchantId}/confirm-pricing-commercials`, {
            stage: 3,
            agreement: {
              gst_stance: agreement.gst_stance,
              vc: agreement.vc,
              weight_ceiling: agreement.weight_ceiling,
              confirmed: agreement.confirmed,
              rvp_enabled: rvp.enabled,
              rvp_mode: rvp.mode === 'forward_linked' ? 'FORMULA' : 'STANDALONE',
              // Mode A terms. The QC charge was never sent at all, and the backend
              // had no field for the flat/multiplier, so all three were lost and the
              // reverse rate fell back to a hard-coded default on the next load.
              rvp_formula_type: rvp.formulas?.__blended__?.type ?? null,
              rvp_flat_charge: rvp.formulas?.__blended__?.flat ?? null,
              rvp_multiplier: rvp.formulas?.__blended__?.mult ?? null,
              rvp_qc_charge: rvp.formulas?.__blended__?.qc ?? null,
              confidence: agreement.confidence,
              detected_services: agreement.detected_services,
              ...(agreement.s3Key ? { s3_key: agreement.s3Key } : {}),
            },
            credit: {
              engagement: credit.engagement,
              cod_split_pct: credit.cod_split_pct,
              prepaid_split_pct: credit.prepaid_split_pct,
              avg_invoice_value: credit.avg_invoice_value,
              credit_exposure: credit.credit_exposure,
              credit_period_days: credit.credit_period_days,
              recommended_limit: credit.recommended_limit,
              sales_override: credit.sales_override,
              ...(credit.approval_s3Key ? { approval_s3_key: credit.approval_s3Key } : {}),
              codRemittance: {
                remit_days: credit.secured_postpaid.remit_days,
                remit_freq_per_week: credit.secured_postpaid.remit_freq_per_week,
                fee_adjust_every: credit.secured_postpaid.fee_adjust_every,
                adjust_window_days: credit.secured_postpaid.adjust_window_days,
              },
            },
            arrangement: effectiveArrangement,
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 4 — Save rate card ──────────────────────────────────────────────
    saveRateCard: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        const wizard = api.getState().pricingWizard;
        const { merchantId, rateCard, effective_date } = wizard;
        if (MOCK) {
          await delay(1000);
          return { data: { success: true, wrappersCreated: [...new Set(rateCard.rows.map(r => r.account_code))].length, rowsSaved: rateCard.rows.length } };
        }
        try {
          // RVP has no separate rate card on the backend — it rides on each forward
          // row as rvp_qc/rvp_noqc. For independent (Mode B) RVP, overlay the editable
          // matrix onto the matching rows so it actually persists. (Mode A rows already
          // carry their formula-derived RVP, so this is a no-op for them.)
          const foldedRows = foldIndependentRvpIntoRows(rateCard.rows, wizard);
          const res = await post(`${apiConstants.MERCHANT}/${merchantId}/save-rate-card`, {
            stage: 4,
            effective_date: effective_date || new Date().toISOString().slice(0, 10),
            is_default: true,
            rateCard: {
              // Strip FE-only fields the backend ignores: parent, service, arrangement, mode, tc_source.
              // GST INCLUSIVE: the preview shows ex-GST (divided) prices, but the backend applies the
              // merchant's gstStance division itself at save — send the RAW inclusive price so the
              // division happens exactly once (backend-side). The backend divides only `price`;
              // RVP charges are stored as sent, so they stay ex-GST here.
              rows: foldedRows.map(({ parent, service, arrangement, mode, tc_source, price_incl, rvp_noqc_incl, rvp_qc_incl, ...row }) => (
                price_incl != null ? { ...row, price: price_incl } : row
              )),
            },
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 5 — Save T&Cs ───────────────────────────────────────────────────
    saveTnc: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        const { pricingWizard: { merchantId, effective_date, effective_mode } } = api.getState();
        // Fields commented out — only DPH-Linked FSC is active on the T&Cs stage.
        // const { invoice_ceiling, rto, tc } = pricingWizard;
        if (MOCK) { await delay(500); return { data: { success: true } }; }
        try {
          // const rtoPayload = { mode: rto.mode };
          // if (rto.mode === 'fixed')   rtoPayload.fixed_amount = rto.fixed_amount;
          // if (rto.mode === 'pct_fwd') rtoPayload.pct_fwd = rto.pct_fwd;
          // if (rto.mode === 'slab')    rtoPayload.slab = rto.slab;

          // const blended = { cod_structure: tc.blended.cod_structure };
          // if (tc.blended.cod_structure === 'tiered_weight')  blended.cod_tiers_weight  = tc.blended.cod_tiers_weight;
          // if (tc.blended.cod_structure === 'tiered_invoice') blended.cod_tiers_invoice = tc.blended.cod_tiers_invoice;

          const res = await post(`${apiConstants.MERCHANT}/${merchantId}/save-tnc`, {
            stage: 5,
            effective_date: effective_date || new Date().toISOString().slice(0, 10),
            effective_mode: effective_mode || 'immediate',
            // invoice_ceiling,   // commented out — not collected on this stage
            // rto: rtoPayload,   // commented out — not collected on this stage
            // tc: { blended },   // commented out — not collected on this stage
          });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 6b — Get onboarding session ────────────────────────────────────
    getOnboardingSession: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        if (MOCK) { await delay(400); return { data: null }; }
        try {
          const res = await bq({ url: `${apiConstants.MERCHANT}/${merchantId}/onboarding-session`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 6c — Commit pricing (scheduling + confirmations) ───────────────
    commitPricingSchedule: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        const { pricingWizard: { merchantId, effective_mode, effective_date, confirmations } } = api.getState();
        if (MOCK) { await delay(500); return { data: { success: true } }; }
        try {
          const body = { stage: 6, effective_mode: effective_mode || 'immediate', confirmations };
          if (effective_mode === 'from_date' && effective_date) body.go_live_date = effective_date;
          const res = await post(`${apiConstants.MERCHANT}/${merchantId}/commit-pricing`, body);
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.effectiveMode) {
            dispatch(setEffectiveMode(data.effectiveMode.toUpperCase() === 'IMMEDIATE' ? 'immediate' : 'from_date'));
          }
          if (data.goLiveDate) dispatch(setEffectiveDate(data.goLiveDate));
        } catch {}
      },
    }),

    // ── Stage 7a — Run pre-flight ─────────────────────────────────────────────
    runPreFlight: builder.query({
      queryFn: async (merchantId, _api, _extra, bq) => {
        if (MOCK) {
          await delay(700);
          return {
            data: {
              clean: true,
              canCommit: true,
              commitBlockReason: null,
              summary: { accountCodes: 0, rateCardRows: 0, effectiveMode: 'IMMEDIATE' },
              checks: { schemaChecksPassed: true, businessRulesChecksPassed: true, errors: [] },
            },
          };
        }
        try {
          const res = await bq({ url: `${apiConstants.MERCHANT}/${merchantId}/pre-flight`, type: 'GET' });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
    }),

    // ── Stage 7b — Execute commit ─────────────────────────────────────────────
    executeCommit: builder.mutation({
      queryFn: async (_arg, api, _extra, bq) => {
        const { pricingWizard: { merchantId } } = api.getState();
        if (MOCK) {
          await delay(1400);
          return { data: { success: true, merchantId, rateCardVersion: 'v1' } };
        }
        try {
          const res = await post(`${apiConstants.MERCHANT}/${merchantId}/execute-commit`, { stage: 7 });
          return { data: res.data ?? res };
        } catch (err) {
          return { error: { status: err?.response?.status, data: err?.response?.data } };
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;
          const { merchant, rateCard, preflight } = getState().pricingWizard;
          dispatch(setMerchant({ ...(merchant || {}), committed: true, status: 'KYC_APPROVED', rateCardVersion: data.rateCardVersion }));
          dispatch(recordConfirmation('commit_to_proship'));
          // Populate wizard.commit so Stage8_Done can render the confirmation screen
          dispatch(setCommit({
            version: data.rateCardVersion,
            written: preflight?.summary?.accountCodes ?? [...new Set(rateCard.rows.map((r) => r.account_code))].length,
            rows: preflight?.summary?.rateCardRows ?? rateCard.rows.length,
            ts: new Date().toISOString(),
            failures: 0,
          }));
        } catch { /* surfaced via isError */ }
      },
    }),

  }),
});

export const {
  useCreateMerchantMutation,
  useUpdateMasterInfoOnlyMutation,
  useRegisterPickupMutation,
  useSubmitKycMutation,
  useCommitRateCardMutation,
  useSaveDraftMutation,
  useLazyLoadMerchantPricingQuery,
  useUpdateBankDetailsMutation,
  useUpdateMerchantProfileMutation,
  useUploadAgreementMutation,
  useUploadCreditApprovalMutation,
  useAnalyzeAgreementMutation,
  useLazyGetAgreementAnalysisQuery,
  useConfirmPricingCommercialsMutation,
  useSaveRateCardMutation,
  useSaveTncMutation,
  useLazyGetOnboardingSessionQuery,
  useCommitPricingScheduleMutation,
  useLazyRunPreFlightQuery,
  useExecuteCommitMutation,
} = wizardApiSlice;
